import { Empty, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';

import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NotificationService from 'core/services/notification';
import { onGetPayments } from 'modules/users/services/users';
import { Payment } from 'modules/users/models/payment';
import { getTopPosition } from 'utils/elementTopPosition';
import { useTranslation } from 'react-i18next';
import NoData from 'components/noData/noData';

interface InputProps {
	id: number;
}

function PaymentsComponent({ id }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [payments, setPayments] = useState<Payment[]>([]);

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	useEffect(() => {
		getPayments();
	}, []);

	const columns: TableProps<Payment>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('USERS.AMOUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (amount) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{amount}</span>,
		},
		{
			title: t('USERS.CURRENCY'),
			dataIndex: 'currency',
			key: 'currency',
			render: (currency) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{currency}</span>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{status}</span>,
		},
		{
			title: t('USERS.DESCRIPTION'),
			dataIndex: 'description',
			key: 'description',
			render: (description) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{description}</span>,
		},
		{
			title: t('USERS.GATEWAY'),
			dataIndex: 'gateway',
			key: 'gateway',
			render: (gateway) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{gateway}</span>,
		},
		{
			title: t('USERS.REFERENCE'),
			dataIndex: 'reference',
			key: 'reference',
			render: (reference) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{reference}</span>,
		},
		{
			title: t('USERS.INVOICE_NO'),
			dataIndex: 'invoice_no',
			key: 'invoice_no',
			render: (invoice_no) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{invoice_no}</span>,
		},
		{
			title: t('USERS.EXPIRE'),
			dataIndex: 'expiry_date',
			key: 'expiry_date',
			render: (expiry_date) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{expiry_date}</span>,
		},
		{
			title: t('USERS.BILLER_CODE'),
			dataIndex: 'biller_code',
			key: 'biller_code',
			render: (biller_code) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{biller_code}</span>,
		},
		{
			title: t('USERS.BILLER_NAME'),
			dataIndex: 'biller_name',
			key: 'biller_name',
			render: (biller_name) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{biller_name}</span>,
		},
		{
			title: t('USERS.UPDATE_TIME'),
			dataIndex: 'updated_at',
			key: 'updated_at',
			render: (updated_at) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{updated_at}</span>,
		},
	];

	const getPayments = async () => {
		try {
			setIsLoading(true);

			const response = await onGetPayments(id);
			setPayments(response.data.data.payments);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error('Ops!', 'An error occurred when fetching payments');
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);

			const response = await onGetPayments(id, page);
			setPayments(response.data.data.payments);

			setTimeout(() => {
				window.scrollTo({ behavior: 'smooth', top: getTopPosition('payments-table') - 65 });
			});

			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error('Ops!', 'An error occurred when fetching payments');
		}
	};

	return (
		<>
			{isLoading ? (
				<LoadingComponent />
			) : (
				<div style={{ position: 'relative' }}>
					<Table
						id="payments-table"
						rowKey="id"
						columns={columns}
						dataSource={payments}
						scroll={{ x: 400 }}
						pagination={{
							pageSize: 15,
							total: totalRecords,
							showSizeChanger: false,
							current: currentPage,
							onChange: fetchMoreData,
						}}
						locale={{ emptyText: <NoData /> }}
					/>

					{isLoadMore && (
						<div className="loadMoreContainer">
							<LoadingComponent />
						</div>
					)}
				</div>
			)}
		</>
	);
}

export default PaymentsComponent;
